import { Component, EventEmitter, Input, OnInit, Output, Signal, inject } from '@angular/core';
import { AbstractControl, FormArray, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FormControlFeedbackComponent, IFormFieldsValidation, Loader, UnsubscriberClass, ValidationServiceHelper } from '@aston/foundation';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntil, tap } from 'rxjs/operators';
import { EMPTY, Observable, identity } from 'rxjs';
import { UpperCasePipe } from '@angular/common';

import { UserRole } from '../../enums';
import { ISubsidiaryUser } from '../../models';

@Component({
    selector: 'middle-users-create-form',
    templateUrl: './middle-users-create-form.component.html',
    imports: [FormsModule, ReactiveFormsModule, TranslateModule, FormControlFeedbackComponent, UpperCasePipe]
})
export class MiddleUsersCreateFormModalComponent extends UnsubscriberClass implements OnInit {
	@Input() usersList: ISubsidiaryUser[] = [];
	@Input() roles: Signal<UserRole[]>;
	@Input() properties: (keyof ISubsidiaryUser)[] = ['userName', 'subsidiaryRole'];
	@Input() validationHelperGetter: Observable<[ValidationServiceHelper, IFormFieldsValidation, string]> = EMPTY;
	@Input() displayEmailAlreadyExistInGuestListError: CallableFunction = identity;
	@Input() displayEmailAlreadyExistInUserListError: CallableFunction = identity;

	@Output() cancel: EventEmitter<boolean> = new EventEmitter();
	@Output() submitForm: EventEmitter<Partial<ISubsidiaryUser>[]> = new EventEmitter();

	form!: UntypedFormGroup;
	userRoles = UserRole;

	public activeModal = inject(NgbActiveModal);
	private formBuilder = inject(UntypedFormBuilder);

	validationHelper!: ValidationServiceHelper;
	rulesetFields!: IFormFieldsValidation;

	formLoader = Loader.empty();
	guestsList = new FormArray<UntypedFormGroup>([]);
	rolesOption = null;

	get userNameField() {
		return this.form.get('userName') as AbstractControl;
	}

	ngOnInit() {
		this.form = this.formBuilder.group({});
		this.form.addControl('userName', new UntypedFormControl());

		this.validationHelperGetter.pipe(
			tap(([helper, fields]) => {
				this.validationHelper = helper;
				this.rulesetFields = fields;
				this.setFormValidators();
			}),
			takeUntil(this.destroySubscriptions$)
		).subscribe();
	}

	setFormValidators() {
		this.validationHelper.control(this.userNameField, this.rulesetFields.UserName);
	}

	addToGuestsList() {
		const guestEmail = this.userNameField.value;

		if (this.alreadyExist(guestEmail)) {
			this.displayEmailAlreadyExistInUserListError();
			return;

		} else if (this.alreadyExistInGuestList(guestEmail)) {
			this.displayEmailAlreadyExistInGuestListError();
			return;
		}

		const group = new UntypedFormGroup({
			userName: new UntypedFormControl(guestEmail),
			subsidiaryRole: new UntypedFormControl(this.userRoles.Operator)
		});

		this.validationHelper.control(group.get('userName'), this.rulesetFields.UserName);

		if (this.properties.includes('firstName')) {
			group.addControl('firstName', new UntypedFormControl())
			this.validationHelper.control(group.get('firstName'), this.rulesetFields.FirstName);
		}

		if (this.properties.includes('lastName')) {
			group.addControl('lastName', new UntypedFormControl())
			this.validationHelper.control(group.get('lastName'), this.rulesetFields.LastName);
		}

		this.guestsList.push(group);
		this.form.reset();
	}

	alreadyExist(guestEmail: string) {
		const found = this.usersList.find(element => element.userName.toLowerCase() === guestEmail.toLowerCase());
		return found;
	}

	alreadyExistInGuestList(guestEmail: string) {
		const found = this.guestsList.value.find(element => element.userName.toLowerCase() === guestEmail.toLowerCase());
		return found;
	}

	removeInvite(index: number) {
		this.guestsList.removeAt(index);
	}

	guestsListIsInvalid() {
		return this.guestsList.controls.some(c => c.get('userName').invalid || c.get('firstName')?.invalid || c.get('lastName')?.invalid);
	}

	onSubmit() {
		this.submitForm.emit(this.guestsList.value);
		this.activeModal.dismiss();
	}
}
